// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- Actions & Hooks
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useDealDROChangesActions } from 'datastage/store/dealDROChanges/useDealDROChanges';
import { useDealDROChangesData } from './hooks/useDealDROChangesData';
// --- External UI Libraries
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
import { Col, Row } from 'antd';
// --- Components
import { PaginationBar } from 'datastage/components/PaginationBar';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
import { DealDROChangesGrid } from './components/grid';
import { DealDROChangesSearchForm } from './components/search-form';
import { TwoRowsLayout } from './layout/two-row';
// --- Utils
import { datastageApi } from 'datastage/api';
import { exportToCsvWithHeaders } from 'datastage/utils/export/exportToCsv';
import { filterCriteria } from 'datastage/utils/filterCriteria';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';

/**
 * Deal DRO Changes Page
 *
 * Features:
 * - Displays a paginated list of Deal DRO Changes
 * - Provides search and export functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 * - Responds to refreshDealDROChanges flag from Redux for grid refresh
 * - Persists page size preference to localStorage automatically
 *
 * @returns {JSX.Element}
 */
export const DealDROChangesPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const {
        fetchDealDROChanges,
        updateDealDROChanges,
        itemsPerPage,
        rangeParam,
        filterForm,
        sortParam,
    } = useDealDROChangesData();
    const { updateDealDROChanges: updateDealDROChangesAction } = useDealDROChangesActions();

    const { totalPages, currentPage, totalItems, refreshDealDROChanges, pageSizeOptions } = useSelector(
        (state) => state.dealDROChanges,
    );

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.dealDROChanges,
        itemsPerPage,
        rangeKey: 'rangeParam',
        onSync: updateDealDROChanges,
    });

    // Handle refreshDealDROChanges flag changes
    useEffect(() => {
        if (refreshDealDROChanges) {
            (async () => {
                const data = await fetchDealDROChanges(rangeParam);
                if (data) {
                    updateDealDROChanges({
                        dealDROChanges: data?.content ?? [],
                        totalPages: data?.totalPages ?? 0,
                        currentPage: data?.pageable?.pageNumber ?? 0,
                        totalItems: data?.totalElements ?? 0,
                        refreshDealDROChanges: false,
                    });
                } else {
                    // Reset flag even on error
                    updateDealDROChangesAction({ refreshDealDROChanges: false });
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshDealDROChanges]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;
        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchDealDROChanges(nextPageRange);
        if (data) {
            updateDealDROChanges({
                dealDROChanges: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                rangeParam: nextPageRange,
                totalItems: data?.totalElements ?? 0,
            });
        }
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    // Refresh the deal DRO changes list
    const handleRefresh = async () => {
        const data = await fetchDealDROChanges(rangeParam);
        if (data) {
            updateDealDROChanges({
                dealDROChanges: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
            });
        }
    };

    /**
     * Export all deal DRO changes to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        try {
            setLoadingTrue();
            const exportRange = getAllItemsRange(totalItems);
            const data = await datastageApi.dealDROChanges(
                filterCriteria(filterForm, '', 'dealDROChanges'),
                exportRange,
                sortParam,
            );

            if (data?.code) {
                console.error('Failed to export deal DRO change data:', data?.message);
                return;
            }

            const headers = [
                'entityId',
                'action',
                'timestamp',
                'timestamp_pdt',
                'catalogId',
                'catalogName',
                'initialReleaseDate',
                'releasingCompany',
                'runtimeInMins',
                'originalLanguage',
                'termStart',
                'termEnd',
                'media',
                'territory',
                'language',
                'description',
                'format',
                'offerTableId',
            ];
            const displayNames = [
                'Right ID',
                'Status',
                'Timestamp (UTC)',
                'Timestamp (Pacific time)',
                'Catalog Id',
                'Catalog Name',
                'Initial Release Date',
                'Releasing Company',
                'Runtime in Mins',
                'Original Language',
                'Term Start',
                'Term End',
                'Media',
                'Territory',
                'Language',
                'Description',
                'Format',
                'Offer Table ID',
            ];
            exportToCsvWithHeaders(data?.content, 'deal_dro_changes_list', headers, displayNames);
        } catch (error) {
            console.error('Error exporting deal DRO change data:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <Typography variant="T50" style={{ marginLeft: 20 }}>
                        Deal DRO Changes
                    </Typography>
                </Col>
                <Col style={{ display: 'flex', gap: 8 }}>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={totalItems === 0}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>
            <TwoRowsLayout>
                <main data-position="top">
                    <DealDROChangesSearchForm />
                </main>
                <main data-position="bottom">
                    <DealDROChangesGrid />
                </main>
            </TwoRowsLayout>
            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />
            <ToastNotificationProvider />
        </>
    );
};
